<template>
    <div>
        <b-modal
            v-model="modalShow"
            title="予約内容を確認"
            class="p-user-reservation"
            size="lg"
            ref="UserReservationConfirmModal"
            hide-footer
            @hidden="close">
            <div v-if="isLoadedState">
                <div class="card mb-3">
                    <div class="card-header">
                        {{ $locale.patient }}情報
                    </div>
                    <div class="card-body">
                        <dl>
                            <dt>{{ $locale.patient }}番号</dt>
                            <dd>{{ user.id }}</dd>

                            <dt>{{ $locale.patient }}氏名</dt>
                            <dd>{{ user.full_name }}<span class="small">（{{ user.full_name_kana }}）</span></dd>

                            <dt>年齢</dt>
                            <dd>{{ user.age }}</dd>

                            <dt>{{ $locale.patient }}メモ</dt>
                            <dd>{{ doctorMemo || '-' }}</dd>
                        </dl>
                    </div>
                </div>

                <div class="card mb-3">
                    <div class="card-header">予約内容</div>
                    <div class="card-body">
                        <dl>
                            <dt>{{ $locale.consultation_host }}科</dt>
                            <dd>{{ medicalCourse.name }}</dd>
                        </dl>
                        <dl>
                            <dt>メニュー</dt>
                            <dd>{{ doctorMenu.title }}</dd>
                        </dl>
                        <dl>
                            <dt>予約日時</dt>
                            <dd
                                class="u-palt"
                                :class="{ 'vc-mb3px' : doctor.timezone !== 'Asia/Tokyo' }">
                                <span>{{ helper.dateForHumanNoConvert(schedule.date) }}</span>
                                <span :class="helper.dayForHumanNoConvertCSS(schedule.date)">
                                    {{ helper.dayForHumanNoConvert(schedule.date, {bracket: true}) }}
                                </span>
                                <span class="ml-1">
                                    {{ helper.displayTimeNoConvert(schedule.start_time, schedule.end_time) }}
                                </span>
                            </dd>
                            <dd v-if="doctor.timezone !== 'Asia/Tokyo' && jstConsultationStart">
                                <div class="vc-timezone-box u-palt">
                                    <span>{{ helper.dateForHumanNoConvert(jstConsultationStart) }}</span>
                                    <span :class="helper.dayForHumanNoConvertCSS(jstConsultationStart)">
                                        {{ helper.dayForHumanNoConvert(jstConsultationStart, { bracket: true }) }}
                                    </span>
                                    <span class="ml-1">{{ jstConsultationTimeFormat }}</span>
                                </div>
                            </dd>
                        </dl>
                    </div>
                </div>

                <template v-if="$locale.type !== 'consul' && $store.state.configs.payment == 1">
                    <div class="card mb-3">
                        <div class="card-header">お支払い方法</div>
                        <div class="card-body">
                            <dl class="mb-0">
                                <dd class="mb-0">
                                    <span>{{ paymentMethod.name }}</span>
                                </dd>
                            </dl>
                        </div>
                    </div>
                    <div class="card mb-3">
                        <div class="card-header">金額</div>
                        <div class="card-body">
                            <dl class="mb-0">
                                <dd class="mb-0">
                                    <span>{{ `${ $locale.consultation_host }終了後に確定` }}</span>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </template>

                <hr>

                <div v-if="completed">
                    <div class="col">
                        <div class="text-center">
                            <p>上記の内容で予約の確定を依頼しました。</p>
                            <div class="d-flex justify-content-center">
                                <div class="btn btn-yokumiru-gray mr-2" @click="close">閉じる</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="row">
                    <div class="col">
                        <div class="text-center">
                            <p>上記の内容で{{ $locale.patient }}様に予約の確定を依頼します。よろしいですか？</p>
                            <div v-if="inProgress">
                                <b-spinner :variant="`yokumiru-user`"></b-spinner>
                            </div>
                            <div v-else class="d-flex justify-content-center">
                                <div class="btn btn-yokumiru-gray mr-2" @click="handleCancel">戻る</div>
                                <div class="btn btn-yokumiru-orange" @click="handleOk">{{ $locale.type === 'consul' ? 'はい' : '予約を確定' }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import constants from '../../../constants'
    import YokumiruHelper from '../../../YokumiruHelper'

    export default {
        props: {
            doctor: {
                type: Object,
                required: true,
            },
            consultationId: {
                type: Number,
                default: null,
            },
            muteCompletedAlert: {
                type: Boolean,
                default: false,
            },
        },

        data: function () {
            return {
                modalShow: false,
                helper: YokumiruHelper,
                paymentMethodCash: constants.payment_methods.cash,
                user: null,
                medicalCourse: null,
                doctorMenu: null,
                schedule: null,
                paymentMethod: null,
                userCoupon: null,
                inProgress: false,
                completed: false,
                doctor_memo: null,
                errors: {},
                estimate: null,
            }
        },

        computed: {
            isLoadedState: function () {
                // doctorMemo, userCoupon は任意のためnullチェックしない
                return this.user !== null
                    && this.doctorMenu !== null
                    && this.medicalCourse !== null
                    && this.schedule !== null
                    && this.paymentMethod !== null
            },

            /**
             * タイムゾーン併記
             * （Asia/Tokyo 診療開始 moment）
             *
             * @return {moment|null}
             */
            jstConsultationStart: function () {
                if (!this.schedule || !this.doctor) {
                    return null
                }

                return this.helper.jstTime(`${this.schedule.date.format('YYYY-MM-DD')} ${this.schedule.start_time}:00`, this.doctor.timezone)
            },

            /**
             * タイムゾーン併記
             * （Asia/Tokyo 診療終了 moment）
             *
             * @return {moment|null}
             */
            jstConsultationEnd: function () {
                if (!this.schedule || !this.doctor) {
                    return null
                }

                return this.jstConsultationStart.clone().add(this.doctorMenu.consultation_minutes, 'minutes')
            },

            /**
             * タイムゾーン併記
             * （Asia/Tokyo 診療時間フォーマット）
             *
             * @return {string}
             */
            jstConsultationTimeFormat: function () {
                if (!this.jstConsultationStart || !this.jstConsultationEnd) {
                    return ''
                }

                let start = this.jstConsultationStart.format('ah:mm')
                let end = this.jstConsultationEnd.format('ah:mm')

                if (start.includes('午前12')) {
                    start = start.replace('午前12', '午前0')
                }
                if (end.includes('午前12')) {
                    end = end.replace('午前12', '午前0')
                }

                return `${start} - ${end} （日本時間）`
            },
        },

        created: function () {
            bus.$on('open-modal-doctor-reservation-confirm', this.open)
        },

        beforeDestroy: function () {
            bus.$off('open-modal-doctor-reservation-confirm')
        },

        methods: {
            /**
             * モーダル表示
             */
            open: function () {
                this.user = this.$store.state.doctorReservation.user
                this.medicalCourse = this.$store.state.doctorReservation.medicalCourse
                this.doctorMenu = this.$store.state.doctorReservation.doctorMenu
                this.doctorMemo = this.$store.state.doctorReservation.doctorMemo
                this.schedule = this.$store.state.doctorReservation.schedule
                this.paymentMethod = this.$store.state.doctorReservation.paymentMethod
                this.userCoupon = this.$store.state.doctorReservation.userCoupon
                this.completed = false

                this.fetchEstimate()
                this.modalShow = true
            },

            /**
             * モーダル非表示
             */
            close: function () {
                this.modalShow = false
            },

            /**
             * 金額（税込）
             *
             * @return {number|void}
             */
            fetchEstimate: function () {
                if (this.$locale.type !== 'consul') {
                    return
                }

                let endpoint = `${constants.doctor.consultations.endpoint.base}/estimate`
                endpoint += `?user_id=${this.user.id}`
                endpoint += `&medical_course_id=${this.medicalCourse.id}`
                endpoint += `&doctor_menu_id=${this.doctorMenu.id}`

                if (this.userCoupon) {
                    endpoint += `&user_coupon_id=${this.userCoupon.id}`
                }

                axios.get(endpoint).then(({data}) => {
                    this.estimate = data.data
                })
            },

            /**
             * 予約ボタン押下時
             */
            handleOk: async function (e) {
                e.preventDefault()

                if (this.inProgress) return
                this.inProgress = true

                const params = {
                    user_id: this.user.id,
                    doctor_id: this.doctor.id,
                    doctor_menu_id: this.doctorMenu.id,
                    payment_method_id: this.paymentMethod.id,
                    start_time: this.startDateTime().format('YYYY-MM-DD HH:mm:ss'),
                    end_time: this.endDateTime().format('YYYY-MM-DD HH:mm:ss'),
                    consultation_id: this.consultationId
                }

                if (this.userCoupon) {
                    params.user_coupon_id = this.userCoupon.id
                }

                await axios.post(constants.doctor.consultations.endpoint.base, params)
                    .then(({data}) => {
                        this.completed = true
                        this.errors = {}
                        if (!this.muteCompletedAlert) {
                            bus.$emit('alert-success', '予約の依頼が完了しました。')
                        }
                        bus.$emit('completed-reservation', data)
                        bus.$emit('close-modal-doctor-reservation-user-search')
                        bus.$emit('close-modal-doctor-reservation-doctor-menu')
                        bus.$emit('close-modal-doctor-reservation-date-select')
                        bus.$emit('close-modal-doctor-reservation-payment-method')
                        bus.$emit('calender-reload')
                    })
                    .catch(({response}) => {
                        if (
                            response
                            && response.hasOwnProperty('data')
                            && response.data.hasOwnProperty('errors')
                        ) {
                            this.errors = response.data.errors
                            if (this.errors.message) {
                                bus.$emit('alert-danger', this.errors.message[0])
                                return
                            } else if (this.errors.payment_method_id) {
                                bus.$emit('alert-danger', this.errors.payment_method_id[0])
                                return
                            }
                        }
                        bus.$emit('alert-danger', 'システムエラーが発生しました。時間を置いて再度お試しください。')
                    })

                this.inProgress = false
            },

            /**
             * 戻るボタン押下時
             *
             * @param {object} e
             */
            handleCancel: function (e) {
                e.preventDefault()
                this.close()
            },

            /**
             * 相談開始日時フォーマット
             *
             * @return {moment}
             */
            startDateTime: function () {
                const schedule = this.$store.state.doctorReservation.schedule

                if (schedule.start_time === '24:00') {
                    schedule.start_time = '00:00'
                }

                const format = schedule.date.format('YYYY-MM-DD')

                return this.helper.jstTime(`${format} ${schedule.start_time}:00`, this.doctor.timezone)
            },

            /**
             * 相談終了日時フォーマット
             *
             * @return {moment}
             */
            endDateTime: function () {
                return this.startDateTime().add(this.doctorMenu.consultation_minutes, 'minutes')
            },
        },
    }
</script>

<style scoped lang="scss">
.invalid-feedback {
    font-size: 14px;
}

.vc-user-coupon {
    .vc-user-coupon__name {
        font-size: .75rem;
        line-height: 1.3;
    }

    .vc-user-coupon__discount {
        margin-left: 1px;
        color: #5f6366;
        font-size: 1.1rem;
        font-weight: bold;
        line-height: 1.3;

        span {
            &:nth-child(2) {
                font-size: .75rem;
            }
        }
    }
}

.p-user-reservation {
    .table {
        th, td {
            font-size: .8rem;
        }
    }
}

.vc-mb3px {
    margin-bottom: 3px;
}

.vc-timezone-box {
    display: inline-block;
    padding: 4px 6px;
    font-size: 13px;
    background-color: rgb(230, 230, 230);
    border-radius: 3px;
}
</style>

<style lang="scss">
.fc-toolbar {
    .fc-center {
        h2 {
            font-size: 1.4rem;
            margin-bottom: 0;
        }
    }
}
</style>
